import type { RaterXPreset, ValueConfig } from '../../api/types/rater-x-preset';
import type { CoverageState } from '../../types';
import type { EditQuoteState } from '../../views/edit-quote/types';
import type { UnderwriteQuoteState } from '../../views/underwriter/types';
import { flattenCoverages } from '../coverages';

/**
 * Merges the coverages from the quoteFieldValues onto the raterXCoverages map.
 *
 * @param quoteFieldValues - The quote field values containing the coverages to be merged.
 * @param raterXCoverages - The raterXCoverages map to merge the coverages onto.
 * @returns The merged coverage map.
 */
export function mergeCoveragesFromQuote(
  quoteFieldValues: EditQuoteState['quote']['dynamicFields'],
  raterXCoverages: RaterXPreset['coverageCategories']
): CoverageState {
  // Step 1
  const coverageMap: CoverageState = flattenCoverages(raterXCoverages).reduce(
    (acc, coverage) => ({
      ...acc,
      [coverage.field.id]: {
        ...coverage,
        isSelected: false,
      },
    }),
    {}
  );

  // Step 2
  quoteFieldValues.forEach((field) => {
    if (coverageMap[field.fieldId] == null) {
      return;
    }

    coverageMap[field.fieldId] = {
      ...coverageMap[field.fieldId],
      ...field,
      value: field.value as ValueConfig,
      isSelected: true,
    };
  });

  // Step 3
  return Object.values(coverageMap).reduce((acc: any, coverage: any) => {
    return {
      ...acc,
      [coverage.key]: coverage,
    };
  }, {});
}

const mergeQuoteCoveragesIntoState = <
  T extends EditQuoteState & Partial<UnderwriteQuoteState>
>(
  data: T
) => {
  const quoteCoverages = mergeCoveragesFromQuote(
    data.quote.dynamicFields,
    data.raterX.coverageCategories
  );

  return {
    ...data,
    coverageState: quoteCoverages,
  };
};

export default mergeQuoteCoveragesIntoState;
